<script setup lang="ts">
import ResetScoreConfirmationModal from '../modals/resetScoreConfirmationModal.vue'

const emit = defineEmits(['scoreUpdated', 'loaded'])

const { $api } = useNuxtApp()
const { t } = useI18n()

const { getError, setErrors } = useErrorHelper()

const checkingForNextGame = ref(true)
const props = defineProps<{ game: Game, tournament: ManageTournament }>()

const localState = ref<Game>(Object.assign({}, props.game))

const nextGame = ref<Game | null>(null)
const useNextGame = ref(false)

if (!localState.value.concluded_at) {
  getBestNextGame()
}
else {
  checkingForNextGame.value = false
}

function showResetScoreModal() {
  if (props.game.phase !== props.tournament.phase || !props.game.can_enter_score) {
    alert('Cannot reset score for this game')
  }
  else {
    useOverlay().create(ResetScoreConfirmationModal, {
      props: {
        game: props.game,
        tournament: props.tournament,
        onConfirm: () => {
          $api('/tournaments/games/reset-score', {
            method: 'post',
            body: {
              game_id: props.game.id,
            },
          }).then(() => {
            useSonner.success(t('Score reset'))
          })
        },
        onCancel: () => {
          useSonner.error(t('Score reset cancelled'))
        },
      },
    }).open()
  }
}

function getScoreOptions() {
  const options = []
  if (props.game.legs_to_win) {
    for (let i = 0; i <= props.game.legs_to_win; i++) {
      options.push({ value: i, label: i.toString() })
    }
  }
  else if (props.game.legs_to_play) {
    for (let i = 0; i <= props.game.legs_to_play; i++) {
      options.push({ value: i, label: i.toString() })
    }
  }
  return options
}

function getBestNextGame() {
  nextGame.value = null

  if (!props.game.tournament_id) {
    return
  }

  $api('/tournaments/games/best-next-game', {
    method: 'post',
    body: {
      game_id: props.game.id,
    },
  }).then((data) => {
    if (data) {
      useNextGame.value = props.tournament.autoplay
      nextGame.value = data
    }
  }).catch((e) => {
    if (e.status === 404) {
      return
    }
    throw e
  }).finally(() => {
    checkingForNextGame.value = false
    emit('loaded')
  })
}

const saveScoreLoading = ref(false)

function saveScore() {
  saveScoreLoading.value = true

  if (!props.game.tournament_id) {
    return
  }

  $api('/tournaments/games/set-score', {
    method: 'post',
    body: {
      game_id: props.game.id,
      next_game_id: useNextGame.value ? nextGame.value?.id : null,
      player1score: {
        sets: 0,
        legs: getPlayer(localState.value.player1).score ?? 0,
        one_eighties: getPlayer(localState.value.player1).one_eighties ?? 0,
        one_seven_ones: getPlayer(localState.value.player1).one_seven_ones ?? 0,
        high_checkouts: getPlayer(localState.value.player1).high_checkouts ?? '',
      },
      player2score: {
        sets: 0,
        legs: getPlayer(localState.value.player2).score ?? 0,
        one_eighties: getPlayer(localState.value.player2).one_eighties ?? 0,
        one_seven_ones: getPlayer(localState.value.player2).one_seven_ones ?? 0,
        high_checkouts: getPlayer(localState.value.player2).high_checkouts ?? '',
      },
    },
  })
    .then(() => {
      emit('scoreUpdated')
    })
    .catch((e) => {
      if (e.status === 422) {
        setErrors(e.data.errors)
      }
      else {
        throw e
      }
    })
    .finally(() => {
      saveScoreLoading.value = false
    })
}
</script>

<template>
  <div
    v-if="checkingForNextGame"
    class="flex row items-center justify-center"
  >
    <Icon
      name="tabler:loader"
      class="animate-spin"
    />
  </div>
  <form
    v-else-if="game.can_enter_score"
    class="flex flex-col justify-between gap-2 p-1 max-w-full"
    @submit.prevent="saveScore"
  >
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-8">
      <div class="flex flex-col gap-2 text-left">
        <div>
          <label for="player1score">{{ $getPlayer(game.player1).name }}</label>
          <br>
          <UiToggleGroup
            v-model="localState.player1!.score"
          >
            <UiToggleGroupItem
              v-for="score in getScoreOptions()"
              :key="score.value"
              :value="score.value"
              size="sm"
              variant="outline"
            >
              {{ score.label }}
            </UiToggleGroupItem>
          </UiToggleGroup>
        </div>
        <div
          v-if="game.track_one_eighties"
          class="flex flex-col"
        >
          <label for="p1.one_eighties">180's</label>
          <UiInput
            v-model="localState.player1!.one_eighties"
            input-id="p1.one_eighties"
            type="number"
            min="0"
            :step="1"
          />
        </div>
        <div
          v-if="game.track_one_seven_ones"
          class="flex flex-col"
        >
          <label for="p1.one_seven_ones">171's</label>
          <UiInput
            v-model="localState.player1!.one_seven_ones"
            input-id="p1.one_seven_ones"
            type="number"
            min="0"
            :step="1"
          />
        </div>
        <div
          v-if="game.track_high_checkouts"
          class="flex flex-col"
        >
          <label for="p1.high_checkouts">{{ $t('High checkouts') }}</label>
          <UiInput
            v-model="localState.player1!.high_checkouts"
            separator=","
            input-id="p2.high_checkouts"
            placeholder="ex: 100,123,170"
          />
        </div>
      </div>
      <div class="flex flex-col gap-2 text-left">
        <div>
          <label for="player2score">{{ $getPlayer(game.player2).name }}</label>
          <br>
          <UiToggleGroup
            v-model="localState.player2!.score"
          >
            <UiToggleGroupItem
              v-for="score in getScoreOptions()"
              :key="score.value"
              :value="score.value"
              variant="outline"
              size="sm"
            >
              {{ score.label }}
            </UiToggleGroupItem>
          </UiToggleGroup>
        </div>
        <div
          v-if="game.track_one_eighties"
          class="flex flex-col"
        >
          <label for="p2.one_eighties">180's</label>
          <UiInput
            v-model="localState.player2!.one_eighties"
            input-id="p2.one_eighties"
            type="number"
            min="0"
            :step="1"
          />
        </div>
        <div
          v-if="game.track_one_seven_ones"
          class="flex flex-col"
        >
          <label for="p2.one_seven_ones">171's</label>
          <UiInput
            v-model="localState.player2!.one_seven_ones"
            input-id="p2.one_seven_ones"
            type="number"
            min="0"
            :step="1"
          />
        </div>
        <div
          v-if="game.track_high_checkouts"
          class="flex flex-col"
        >
          <label for="p2.high_checkouts">{{ $t('High checkouts') }}</label>
          <UiInput
            v-model="localState.player2!.high_checkouts"
            separator=","
            input-id="p2.high_checkouts"
            placeholder="ex: 100,123,170"
          />
        </div>
      </div>
    </div>

    <UiAlert
      v-if="getError('player1score')"
      variant="warning"
      :title="getError('player1score')"
    />

    <div
      v-if="nextGame"
      class="flex flex-col gap-2"
    >
      <UiSeparator />
      <div>
        <template v-if="nextGame.writer.id === 0">
          {{ $t('Use board and writer (loser) for next game?') }}
        </template>
        <template v-else>
          {{ $t('Use board for next game (already has writer assigned)?') }}
        </template>
      </div>
      <div class="flex flex-row gap-2 items-center">
        <UiSwitch
          id="useNextGame"
          v-model="useNextGame"
          name="useNextGame"
        />
        <UiLabel for="useNextGame">
          {{ nextGame.label }}
        </UiLabel>
      </div>
    </div>
    <div class="flex justify-between">
      <UiDropdownMenu>
        <UiDropdownMenuTrigger>
          <UiButton
            size="sm"
            icon="tabler-dots"
            variant="secondary"
            :text="$t('Actions')"
          />
        </UiDropdownMenuTrigger>
        <UiDropdownMenuContent>
          <UiDropdownMenuItem
            v-if="game.can_enter_score"
            icon="tabler-trash"
            class="text-red-500"
            :title="$t('Reset score')"
            @click="showResetScoreModal"
          />
        </UiDropdownMenuContent>
      </UiDropdownMenu>
      <UiButton
        :loading="saveScoreLoading"
        autofocus
        size="sm"
        type="submit"
      >
        <template v-if="useNextGame">
          {{ $t('Save and use next game') }}
        </template>
        <template v-else>
          {{ $t('Save') }}
        </template>
      </UiButton>
    </div>
  </form>
  <template v-else>
    <div class="p-4">
      <template v-if="game.concluded_at">
        {{ $t('Can no longer modify score') }}
      </template>
      <template v-else>
        {{ $t('Missing players') }}
      </template>
    </div>
  </template>
</template>
